import { useState } from 'react';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  type ButtonProps,
  Container,
  Drawer,
  IconButton,
  Stack,
  styled,
} from '@mui/material';
import type {
  CourseTypesMenuProps,
  KnowledgeAreasMenuProps,
  MobileMenuProps,
} from './interfaces';
import { MainLogo } from '@/components/layout/header/components/main-logo';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { useCatalogMenu } from '@/hooks/use-catalog-menu';
import { useNavigate } from '@/hooks/use-navigation';

const StyledButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 400,
  textAlign: 'left',
  justifyContent: 'space-between',
  mb: 1,
}));

const CourseTypesMenu = ({
  courseTypes,
  onBack,
  onOpenKnowledge,
  closeMenu,
}: CourseTypesMenuProps) => {
  const navigate = useNavigate();
  return (
    <Stack mt={3}>
      <Button sx={{ justifyContent: 'left', gap: 3 }} onClick={onBack}>
        <ChevronLeft fontSize="small" />
        Cursos
      </Button>
      <StyledButton onClick={onOpenKnowledge} fullWidth>
        Temas
        <ChevronRight fontSize="small" />
      </StyledButton>
      <StyledButton
        onClick={() => {
          navigate('/cursos/?free_courses=true');
          closeMenu();
        }}
      >
        Cursos Gratuitos
      </StyledButton>
      {courseTypes?.map((item) => (
        <StyledButton
          key={item.id}
          onClick={() => {
            navigate(item.route);
            closeMenu();
          }}
          fullWidth
        >
          {item.label}
        </StyledButton>
      ))}
    </Stack>
  );
};

const KnowledgeAreasMenu = ({
  knowledgeAreas,
  onBack,
  closeMenu,
}: KnowledgeAreasMenuProps) => {
  const navigate = useNavigate();
  return (
    <Stack mt={3}>
      <Button sx={{ justifyContent: 'left', gap: 3 }} onClick={onBack}>
        <ChevronLeft fontSize="small" />
        Temas
      </Button>
      {knowledgeAreas?.map((item) => (
        <StyledButton
          key={item.id}
          onClick={() => {
            navigate(item.route);
            closeMenu();
          }}
          fullWidth
        >
          {item.label}
        </StyledButton>
      ))}
    </Stack>
  );
};

type View = 'root' | 'courseTypes' | 'knowledge';

export const MobileMenu = ({
  open,
  onClose,
  visibleMenu,
  onMenuItemClick,
}: MobileMenuProps) => {
  const navigate = useNavigate();
  const bootstrap = useBootstrapData();
  const { courseTypes, knowledgeAreas } = useCatalogMenu();
  const [view, setView] = useState<View>('root');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: '85vw' } } }}
      variant="temporary"
    >
      <Container sx={{ my: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <IconButton
            aria-label="Fechar menu"
            onClick={onClose}
            sx={{ display: { xs: 'flex', lg: 'none' } }}
          >
            <Close />
          </IconButton>
          <Box
            onClick={() => {
              navigate('/');
              onClose();
            }}
            sx={{ all: 'unset', cursor: 'pointer' }}
          >
            <MainLogo />
          </Box>
        </Stack>
        {view === 'root' && (
          <Stack mt={3}>
            {!bootstrap?.isFetching &&
              visibleMenu.map((item) => {
                const courses = item.label === 'Cursos';
                return (
                  <StyledButton
                    key={item.label}
                    onClick={() =>
                      courses ? setView('courseTypes') : onMenuItemClick(item)
                    }
                  >
                    {item.label}
                    {courses && <ChevronRight fontSize="small" />}
                  </StyledButton>
                );
              })}
          </Stack>
        )}

        {view === 'courseTypes' && (
          <CourseTypesMenu
            courseTypes={courseTypes}
            onBack={() => setView('root')}
            onOpenKnowledge={() => setView('knowledge')}
            closeMenu={onClose}
          />
        )}

        {view === 'knowledge' && (
          <KnowledgeAreasMenu
            knowledgeAreas={knowledgeAreas}
            onBack={() => setView('courseTypes')}
            closeMenu={onClose}
          />
        )}
      </Container>
    </Drawer>
  );
};
